import { useMemo } from "react";

import {
  buildLeadHeuteUebersicht,
  leadSichten,
  type LeadHeuteUebersicht,
  type LeadSicht,
} from "@shared/leads/leadsHeute";
import type { NotificationItem } from "@shared/schemas/notifications";

import { useNotifications } from "@/hooks/useNotifications";
import { useOpenLeads } from "@/hooks/useOpenLeads";
import { useUnassignedLeads } from "@/hooks/useUnassignedLeads";
import { useAuth } from "@/lib/auth/AuthContext";

// ---------------------------------------------------------------------------
// useLeadsHeute — die Leads-Seite, die Zahl am Menuepunkt und die Zeile auf
// der Startseite lesen DIESELBE Uebersicht (LEAD-25)
// ---------------------------------------------------------------------------
// Drei Abfragen, die es schon gibt (offene Anfragen, Karten, «Wer
// übernimmt?»), eine Regel (`shared/leads/leadsHeute.ts`). Ohne `sicht`
// gilt der Standard des Menschen — so zaehlt die Leiste immer gleich, egal
// was auf der Seite gerade umgeschaltet ist.

export interface LeadsHeute {
  readonly laedt: boolean;
  readonly fehler: boolean;
  readonly neuLaden: () => void;
  readonly sicht: LeadSicht;
  readonly standardSicht: LeadSicht;
  readonly waehlbar: readonly LeadSicht[];
  readonly uebersicht: LeadHeuteUebersicht<NotificationItem> | null;
  readonly koordinatorUserId: string | null;
  /** Anfragen in der Ablage «Gelöscht» — fuer den Sprung dorthin. */
  readonly ablageAnzahl: number;
}

const LEER: ReadonlySet<string> = new Set();

export function useLeadsHeute(gewaehlt?: LeadSicht): LeadsHeute {
  const { user } = useAuth();
  const openLeads = useOpenLeads();
  const notifications = useNotifications();
  // Laeuft nur fuer Verteiler (das Gate steht im Hook).
  const unassigned = useUnassignedLeads();

  const koordinatorUserId = openLeads.data?.koordinatorUserId ?? null;
  const sichten = useMemo(
    () =>
      user
        ? leadSichten(user, koordinatorUserId)
        : { standard: "pl" as const, waehlbar: ["pl" as const] },
    [user, koordinatorUserId],
  );
  const sicht =
    gewaehlt !== undefined && sichten.waehlbar.includes(gewaehlt)
      ? gewaehlt
      : sichten.standard;

  const uebersicht = useMemo(() => {
    if (!user || !openLeads.data) return null;
    const zuVerteilen = unassigned.data
      ? new Set(unassigned.data.leads.map((l) => l.projectId))
      : LEER;
    return buildLeadHeuteUebersicht<NotificationItem>(
      openLeads.data.leads,
      notifications.data?.items ?? [],
      { userId: user.id, sicht },
      zuVerteilen,
      new Date(),
    );
  }, [user, openLeads.data, notifications.data, unassigned.data, sicht]);

  return {
    laedt: openLeads.isLoading || notifications.isLoading,
    fehler: openLeads.isError,
    neuLaden: () => void openLeads.refetch(),
    sicht,
    standardSicht: sichten.standard,
    waehlbar: sichten.waehlbar,
    uebersicht,
    koordinatorUserId,
    ablageAnzahl: openLeads.data?.ablageAnzahl ?? 0,
  };
}
